import React, { useState } from 'react';
import { Bell, AlertTriangle, AlertCircle, Info, X, Check, Eye } from 'lucide-react';
import { useAlerts } from '../../hooks/useAlerts';
import './AlertNotificationCenter.css';

/**
 * Alert Notification Center
 * Displays active alerts in a dropdown panel
 */
const AlertNotificationCenter = ({ userId, userName, onViewAll }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'critical', 'high', 'medium'
  
  const { 
    alerts, 
    loading, 
    counts, 
    markAsRead, 
    acknowledge, 
    dismiss,
    refresh 
  } = useAlerts({ 
    userId, 
    activeOnly: true,
    autoRefresh: true,
    refreshInterval: 30000 
  });
  
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return <AlertTriangle size={20} color="#f44336" />;
      case 'HIGH':
        return <AlertCircle size={20} color="#ff9800" />;
      case 'MEDIUM':
        return <Info size={20} color="#ffc107" />;
      default:
        return <Bell size={20} color="#2196f3" />;
    }
  };
  
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL':
        return '#f44336';
      case 'HIGH':
        return '#ff9800';
      case 'MEDIUM':
        return '#ffc107';
      default:
        return '#2196f3';
    }
  };
  
  const handleAlertClick = async (alert) => {
    if (!alert.read) {
      await markAsRead(alert.id);
    }
  };
  
  const handleAcknowledge = async (e, alertId) => {
    e.stopPropagation();
    await acknowledge(alertId, userName);
  };
  
  const handleDismiss = async (e, alertId) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to dismiss this alert?')) {
      await dismiss(alertId, 'Dismissed by user');
    }
  };
  
  const getFilteredAlerts = () => {
    if (filter === 'all') {
      return alerts;
    }
    return alerts.filter(a => a.severity === filter.toUpperCase());
  };
  
  const filteredAlerts = getFilteredAlerts();
  const unreadCount = counts.unread;
  
  
  return (
    <div className="alert-notification-center">
      {/* Bell Button */}
      <button
        className={`alert-bell-button ${unreadCount > 0 ? 'has-alerts' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="alert-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      
      {/* Dropdown Panel */}
      {isOpen && (
        <>
          <div className="alert-overlay" onClick={() => setIsOpen(false)} />
          <div className="alert-dropdown-panel">
            {/* Header */}
            <div className="alert-panel-header">
              <div className="header-title">
                <Bell size={20} />
                <h3>Notifications</h3>
                {unreadCount > 0 && (
                  <span className="unread-badge">{unreadCount} new</span>
                )}
              </div>
              <button 
                className="close-button"
                onClick={() => setIsOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
            
            
            {/* Alert Counts */}
            <div className="alert-counts">
              <div className="count-item critical">
                <span className="count-number">{counts.critical}</span>
                <span className="count-label">Critical</span>
              </div>
              <div className="count-item high">
                <span className="count-number">{counts.high}</span>
                <span className="count-label">High</span>
              </div>
              <div className="count-item medium">
                <span className="count-number">{counts.medium}</span>
                <span className="count-label">Medium</span>
              </div>
            </div>
            
            {/* Filters */}
            <div className="alert-filters">
              <button
                className={`btn btn-secondary ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All ({alerts.length})
              </button>
              <button
                className={`btn btn-secondary ${filter === 'critical' ? 'active' : ''}`}
                onClick={() => setFilter('critical')}
              >
                Critical ({counts.critical})
              </button>
              <button
                className={`btn btn-secondary ${filter === 'high' ? 'active' : ''}`}
                onClick={() => setFilter('high')}
              >
                High ({counts.high})
              </button>
            </div>
            
            {/* Alerts List */}
            <div className="alerts-list">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner" />
                  <p>Loading notifications...</p>
                </div>
              ) : filteredAlerts.length === 0 ? (
                <div className="empty-state">
                  <Bell size={48} color="#ccc" />
                  <p>No {filter !== 'all' ? filter : ''} notifications</p>
                </div>
              ) : (
                filteredAlerts.map(alert => (
                  <div
                    key={alert.id}
                    className={`alert-item ${alert.severity.toLowerCase()} ${!alert.read ? 'unread' : ''}`}
                    onClick={() => handleAlertClick(alert)}
                    style={{
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                    }}
                  >
                    <div className="alert-icon">
                      {getSeverityIcon(alert.severity)}
                    </div>
                    
                    <div className="alert-content">
                      <div className="alert-title">
                        {alert.title}
                        {!alert.read && <span className="unread-dot" />}
                      </div>
                      
                      <div className="alert-message">
                        {alert.message}
                      </div>
                      
                      <div className="alert-meta">
                        <span className="alert-time">
                          {formatTimeAgo(alert.createdAt)}
                        </span>
                        {alert.acknowledgedBy && alert.acknowledgedBy.length > 0 && (
                          <span className="acknowledged-badge">
                            ✓ Acknowledged by {alert.acknowledgedBy.length}
                          </span>
                        )}
                      </div>
                      
                      <div className="alert-actions">
                        {!alert.acknowledgedBy?.some(ack => ack.userId === userId) && (
                          <button
                            className="btn btn-success btn-icon"
                            onClick={(e) => handleAcknowledge(e, alert.id)}
                            title="Acknowledge"
                          >
                            <Check size={14} />
                            Acknowledge
                          </button>
                        )}
                        <button
                          className="btn btn-secondary btn-icon"
                          onClick={(e) => handleDismiss(e, alert.id)}
                          title="Dismiss"
                        >
                          <X size={14} />
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Footer */}
            <div className="alert-panel-footer">
              <button
                className="view-all-button"
                onClick={() => {
                  setIsOpen(false);
                  if (onViewAll) onViewAll();
                }}
              >
                <Eye size={16} />
                View All Alerts
              </button>
              <button
                className="refresh-button"
                onClick={refresh}
                title="Refresh"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Format timestamp to human-readable relative time
 */
function formatTimeAgo(timestamp) {
  if (!timestamp) return 'Just now';
  
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp.toDate?.() || timestamp;
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return date.toLocaleDateString();
}

export default AlertNotificationCenter;

